import { bucketDrops, filterKey, type Attack, type NodeBucket, type TrafficModel } from "./model";
import type { Band, ChartSeries } from "@/components/charts/TimeSeriesChart";

export const MAX_SLOTS = 8;

/**
 * Categorical slot per node, by first-seen order — so a node keeps its color
 * for the whole session no matter how many nodes come or go. With more than
 * eight nodes the first seven keep slots 1-7 and the rest fold into "Khác"
 * (slot 8); hues are never cycled.
 */
export function nodeSlots(order: string[]): { key: string; slot: number; folded: boolean }[] {
  const fold = order.length > MAX_SLOTS;
  return order.map((key, i) => {
    if (fold && i >= MAX_SLOTS - 1) return { key, slot: MAX_SLOTS, folded: true };
    return { key, slot: i + 1, folded: false };
  });
}

export const OTHER_KEY = "__other__";

export type TrafficMetric = "pps" | "bps";

export interface TrafficChartData {
  xs: number[];
  series: ChartSeries[];
  latest: Map<string, number>; // series key -> latest non-null value
}

/** Minimum seconds shown, so a freshly opened panel isn't one sliver at the right edge. */
const MIN_SPAN_SEC = 60;

/** The x range: the configured window, trimmed to where data exists (but at least MIN_SPAN_SEC). */
export function chartRange(model: TrafficModel, nowSec: number): { start: number; end: number } {
  const end = Math.max(nowSec, model.buckets[model.buckets.length - 1]?.t ?? nowSec);
  const windowStart = end - model.windowSec + 1;
  const first = model.buckets[0]?.t ?? end;
  const start = Math.max(windowStart, Math.min(first, end - MIN_SPAN_SEC + 1));
  return { start, end };
}

/** Contiguous per-second x axis over the window; missing seconds are gaps (null). */
export function buildTrafficChart(model: TrafficModel, metric: TrafficMetric, nowSec: number): TrafficChartData {
  const { start, end } = chartRange(model, nowSec);
  const xs: number[] = [];
  for (let t = start; t <= end; t++) xs.push(t);
  const byT = new Map(model.buckets.map((b) => [b.t, b]));

  const slots = nodeSlots(model.nodeOrder);
  const seriesKeys: { key: string; label: string; slot: number; members: string[] }[] = [];
  for (const s of slots) {
    if (s.folded) {
      const other = seriesKeys.find((x) => x.key === OTHER_KEY);
      if (other) other.members.push(s.key);
      else seriesKeys.push({ key: OTHER_KEY, label: "Khác", slot: MAX_SLOTS, members: [s.key] });
    } else {
      seriesKeys.push({ key: s.key, label: model.nodeNames.get(s.key) ?? s.key, slot: s.slot, members: [s.key] });
    }
  }

  const latest = new Map<string, number>();
  const series: ChartSeries[] = seriesKeys.map((sk) => {
    const values = xs.map((t) => {
      const b = byT.get(t);
      if (!b) return null;
      let sum = 0;
      let any = false;
      for (const m of sk.members) {
        const nb: NodeBucket | undefined = b.nodes.get(m);
        if (nb) {
          sum += nb[metric];
          any = true;
        }
      }
      return any ? sum : null;
    });
    for (let i = values.length - 1; i >= 0; i--) {
      const v = values[i];
      if (v != null) {
        latest.set(sk.key, v);
        break;
      }
    }
    return { key: sk.key, label: sk.label, colorVar: `--series-${sk.slot}`, values };
  });

  return { xs, series, latest };
}

export function buildDropsChart(model: TrafficModel, nowSec: number): TrafficChartData {
  const { start, end } = chartRange(model, nowSec);
  const xs: number[] = [];
  const rl: (number | null)[] = [];
  const bw: (number | null)[] = [];
  const byT = new Map(model.buckets.map((b) => [b.t, b]));
  for (let t = start; t <= end; t++) {
    xs.push(t);
    const b = byT.get(t);
    if (!b) {
      rl.push(null);
      bw.push(null);
      continue;
    }
    const d = bucketDrops(b);
    rl.push(d.ratelimit);
    bw.push(d.bandwidth);
  }
  const lastOf = (a: (number | null)[]) => {
    for (let i = a.length - 1; i >= 0; i--) if (a[i] != null) return a[i]!;
    return 0;
  };
  return {
    xs,
    series: [
      { key: "ratelimit", label: "Vượt ngưỡng pps", colorVar: "--series-1", values: rl },
      { key: "bandwidth", label: "Vượt băng thông", colorVar: "--series-2", values: bw },
    ],
    latest: new Map([
      ["ratelimit", lastOf(rl)],
      ["bandwidth", lastOf(bw)],
    ]),
  };
}

/** Current fleet totals from each node's newest bucket within the last few seconds. */
export function fleetNow(model: TrafficModel, nowMs: number): { pps: number; bps: number; dropped: number; nodes: number; fresh: boolean } {
  const recent = model.buckets.filter((b) => b.t >= Math.floor(nowMs / 1000) - 5);
  const perNode = new Map<string, NodeBucket>();
  for (const b of recent) for (const [k, v] of b.nodes) perNode.set(k, v); // later buckets win
  let pps = 0;
  let bps = 0;
  let dropped = 0;
  for (const v of perNode.values()) {
    pps += v.pps;
    bps += v.bps;
    dropped += v.droppedRatelimit + v.droppedBandwidth;
  }
  return { pps, bps, dropped, nodes: perNode.size, fresh: perNode.size > 0 };
}

export function attackBands(attacks: Attack[], nowMs: number): Band[] {
  return attacks
    .filter((a) => a.startedAtMs !== undefined)
    .map((a) => ({ start: a.startedAtMs! / 1000, end: (a.endedAtMs ?? nowMs) / 1000 }));
}

export type FilterMetric = "pps" | "bps" | "drops";

/** A filter keeps one color everywhere: its kernel index picks the slot. */
export function filterSlot(kernelIndex: number): number {
  return (kernelIndex % MAX_SLOTS) + 1;
}

/**
 * One node's traffic split by filter: a line per filter that node has
 * reported. "drops" is packets/s dropped by that filter (pps + bandwidth).
 * Lines are not additive: a packet passes `default` and maybe one more.
 */
export function buildFilterChart(model: TrafficModel, node: string, metric: FilterMetric, nowSec: number): TrafficChartData {
  const { start, end } = chartRange(model, nowSec);
  const xs: number[] = [];
  for (let t = start; t <= end; t++) xs.push(t);
  const byT = new Map(model.buckets.map((b) => [b.t, b]));
  const indexes = [...(model.nodeFilters.get(node) ?? [])].sort((a, b) => a - b);
  const latest = new Map<string, number>();
  const series: ChartSeries[] = indexes.map((idx) => {
    const key = filterKey(node, idx);
    const values = xs.map((t) => {
      const f = byT.get(t)?.filters.get(key);
      if (!f) return null;
      return metric === "drops" ? f.droppedRatelimit + f.droppedBandwidth : f[metric];
    });
    for (let i = values.length - 1; i >= 0; i--) {
      const v = values[i];
      if (v != null) {
        latest.set(key, v);
        break;
      }
    }
    return { key, label: model.filterNames.get(idx) ?? `#${idx}`, colorVar: `--series-${filterSlot(idx)}`, values };
  });
  return { xs, series, latest };
}
